/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ClipLoader } from 'react-spinners';
import { tokenStore } from 'sharetribe-flex-sdk';
import { getResaleHubMagicLink } from '../../../util/api';

import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { ensureCurrentUser } from '../../../util/data';
import { offersFeature } from '../../../config/configOffers';

import {
  AvatarLarge,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  SettingsPopup,
} from '../../../components';

import css from './TopbarMobileMenu.module.css';

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    onLogout,
  } = props;

  const [isLoadingSellLink, setIsLoadingSellLink] = useState(false);

  const user = ensureCurrentUser(currentUser);
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const handleSellLinkClick = async e => {
    e.preventDefault();

    if (!isAuthenticated) {
      window.location.href = '/login';
      return;
    }

    setIsLoadingSellLink(true);

    try {
      const response = await getResaleHubMagicLink();
      window.open(response.magicLink, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('SellLink error:', err);
    } finally {
      setIsLoadingSellLink(false);
    }
  };

  const resaleHubSellButtonsEnabled =
    process.env.REACT_APP_RESALE_HUB_SELL_BUTTONS_ENABLED === 'true';

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          {/* <MultiLanguageSelect /> */}
          <SettingsPopup currentPage={currentPage} />

          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('SearchPage'))}
            name="SearchPage"
            to={
              currentPage === 'SearchPage'
                ? { pathname: '/shop', search: '?refresh=true' }
                : undefined
            }
          >
            <FormattedMessage id="TopbarMobileMenu.shop" />
          </NamedLink>
          {resaleHubSellButtonsEnabled ? (
            <a href="#" onClick={handleSellLinkClick} className={css.navigationLink}>
              <FormattedMessage id="TopbarMobileMenu.sell" />
              {isLoadingSellLink && (
                <ClipLoader color="currentColor" size={10} className={css.loadingSpinner} />
              )}
            </a>
          ) : (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('SellHomePage'))}
              name="SellHomePage"
            >
              <FormattedMessage id="TopbarMobileMenu.sell" />
            </NamedLink>
          )}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('HowItWorksPage'))}
            name="HowItWorksPage"
          >
            <FormattedMessage id="TopbarMobileMenu.howItWorks" />
          </NamedLink>
          <a href="https://www.marshall.com/gb/en" className={css.navigationLink}>
            <span>
              <FormattedMessage id="TopbarMobile.brandLink" />
            </span>
          </a>
        </div>
        <div className={css.footer}>
          {resaleHubSellButtonsEnabled ? (
            <a href="#" onClick={handleSellLinkClick} className={css.createNewListingLink}>
              <FormattedMessage id="TopbarMobileMenu.newListingLink" />
              {isLoadingSellLink && (
                <ClipLoader color="white" size={10} className={css.loadingSpinner} />
              )}
            </a>
          ) : (
            <NamedLink name="SellHomePage" className={css.createNewListingLink}>
              <FormattedMessage id="TopbarMobileMenu.newListingLink" />
            </NamedLink>
          )}
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;

  return (
    <div className={css.root}>
      {/* <MultiLanguageSelect /> */}
      <SettingsPopup currentPage={currentPage} />

      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('SearchPage'))}
          name="SearchPage"
          to={
            currentPage === 'SearchPage'
              ? { pathname: '/shop', search: '?refresh=true' }
              : undefined
          }
        >
          <FormattedMessage id="TopbarMobileMenu.shop" />
        </NamedLink>
        {resaleHubSellButtonsEnabled ? (
          <a
            href="#"
            onClick={handleSellLinkClick}
            className={classNames(css.navigationLink, currentPageClass('SellHomePage'))}
          >
            <FormattedMessage id="TopbarMobileMenu.sell" />
            {isLoadingSellLink && (
              <ClipLoader color="currentColor" size={10} className={css.loadingSpinner} />
            )}
          </a>
        ) : (
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('SellHomePage'))}
            name="SellHomePage"
          >
            <FormattedMessage id="TopbarMobileMenu.sell" />
          </NamedLink>
        )}
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('HowItWorksPage'))}
          name="HowItWorksPage"
        >
          <FormattedMessage id="TopbarMobileMenu.howItWorks" />
        </NamedLink>
        <a href="https://www.marshall.com/gb/en" className={css.navigationLink}>
          <span>
            <FormattedMessage id="TopbarMobile.brandLink" />
          </span>
        </a>
        <NamedLink
          className={classNames(css.inbox, currentPageClass('InboxPage'))}
          name="InboxPage"
          params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
        >
          <FormattedMessage id="TopbarMobileMenu.inboxLink" />
          {notificationCountBadge}
        </NamedLink>
        {offersFeature && (
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('OffersPage'))}
            name="OffersPage"
          >
            <FormattedMessage id="TopbarMobileMenu.yourOffersLink" />
          </NamedLink>
        )}
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
          name="ManageListingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
          name="ProfileSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
          name="AccountSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
        </NamedLink>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>
        {resaleHubSellButtonsEnabled ? (
          <a href="#" onClick={handleSellLinkClick} className={css.createNewListingLink}>
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
            {isLoadingSellLink && (
              <ClipLoader color="white" size={10} className={css.loadingSpinner} />
            )}
          </a>
        ) : (
          <NamedLink name="SellHomePage" className={css.createNewListingLink}>
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        )}
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
